<template>
  <div class="markdown-editor">
    <!-- Preview Mode -->
    <div
      v-if="!isEditing"
      @click="toggleEdit"
      class="markdown-preview hover:tw-bg-primary-50 hover:tw-cursor-pointer tw-mb-2 tw-p-4 tw-border tw-border-gray-300 tw-rounded-lg tw-transition-all tw-duration-200 tw-min-h-[272px] tw-max-h-[272px] tw-overflow-y-auto"
      v-html="renderedContent"
      :data-placeholder="$t('markdownEditor.placeholder')"
    ></div>

    <!-- Edit Mode -->
    <div v-if="isEditing" class="tw-relative tw-mb-2">
      <!-- Indicador de edição -->
      <!-- <div class="tw-absolute tw-top-2 tw-right-2 tw-z-10">
        <v-chip size="small" color="primary" variant="flat" class="tw-text-xs">
          <v-icon size="small" class="tw-mr-1">mdi-pencil</v-icon>
          Editando
        </v-chip>
      </div> -->

      <!-- auto-grow -->
      <v-textarea
        v-model="editContent"
        :label="label"
        :placeholder="placeholder"
        variant="outlined"
        color="primary"
        rounded="lg"
        rows="10"
        :autofocus="true"
        hide-details
        @blur="handleSave"
        @keydown.ctrl.enter="handleSave"
        @keydown.esc="handleCancel"
        @input="handleInput"
        class="tw-mb-0"
        :disabled="disabled"
      />

      <!-- <div class="tw-absolute tw-top-2 tw-right-2 tw-flex tw-gap-1">
        <v-tooltip text="Salvar (Ctrl+Enter)">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              size="small"
              icon="mdi-check"
              color="success"
              variant="flat"
              @click="handleSave"
            />
          </template>
        </v-tooltip>

        <v-tooltip text="Cancelar (Esc)">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              size="small"
              icon="mdi-close"
              color="error"
              variant="flat"
              @click="handleCancel"
            />
          </template>
        </v-tooltip>
      </div> -->
    </div>

    <!-- Dicas de markdown -->
    <div
      v-if="showTips"
      class="tw-text-xs tw-text-gray-500 tw-flex tw-flex-wrap tw-gap-2 markdown-tips"
    >
      <span class="tw-font-bold tw-px-2 tw-py-1 tw-w-full sm:tw-w-auto">
        {{ $t('markdownEditor.formattingTips') }}
      </span>
      <span
        class="tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded"
        @click="insertMarkdown('**texto**')"
      >
        {{ $t('markdownEditor.tips.bold') }}
      </span>
      <span
        class="tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded"
        @click="insertMarkdown('*texto*')"
      >
        {{ $t('markdownEditor.tips.italic') }}
      </span>
      <span
        class="tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded"
        @click="insertMarkdown('### Título')"
      >
        {{ $t('markdownEditor.tips.title') }}
      </span>
      <span
        class="tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded"
        @click="insertMarkdown('- Item da lista')"
      >
        {{ $t('markdownEditor.tips.list') }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { renderMarkdown } from '@/utils/markdownRenderer'
import { useI18n } from '@/composables/useI18n'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  renderMarkdownTitle: {
    type: Boolean,
    default: false,
  },
  showTips: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:modelValue'])

const { t } = useI18n()
const isEditing = ref(false)
const editContent = ref('')

// Computed para renderizar o markdown
const renderedContent = computed(() => {
  if (!props.modelValue || props.modelValue.trim() === '') {
    return `<p class="tw-text-gray-400 tw-italic">${props.placeholder}</p>`
  }
  return renderMarkdown(props.modelValue, t, props.renderMarkdownTitle)
})

// Iniciar edição
const toggleEdit = () => {
  isEditing.value = true
  editContent.value = props.modelValue
}

// Salvar conteúdo
const handleSave = async () => {
  isEditing.value = false
  emit('update:modelValue', editContent.value)
}

// Cancelar edição
const handleCancel = () => {
  isEditing.value = false
  editContent.value = props.modelValue // Restaurar valor original
}

// Inserir markdown no editor
const insertMarkdown = (markdown) => {
  const textarea = document.querySelector('.markdown-editor textarea')
  if (textarea) {
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const text = editContent.value

    const before = text.substring(0, start)
    const after = text.substring(end, text.length)

    editContent.value = before + markdown + after

    // Focar no textarea e posicionar cursor
    nextTick(() => {
      textarea.focus()
      textarea.setSelectionRange(
        start + markdown.length,
        start + markdown.length,
      )
    })
  }
}

// Preview em tempo real durante edição
const handleInput = () => {
  // Atualizar preview em tempo real se necessário
  // Por enquanto, apenas manter o estado sincronizado
}

// Resetar estado quando modelValue mudar
watch(
  () => props.modelValue,
  (newValue) => {
    if (!isEditing.value) {
      editContent.value = newValue
    }
  },
  { immediate: true },
)
</script>

<style scoped lang="scss">
.markdown-editor {
  .markdown-preview {
    min-height: 120px;
    border-radius: 8px;
    border: 1px solid #d1d5db; /* Borda padrão de inputs não ativos */

    &:empty::before {
      content: attr(data-placeholder);
      color: #9ca3af;
      font-style: italic;
      display: block;
      padding: 2rem 0;
      text-align: center;
    }

    /* Estilização do scroll */
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f5f9;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;

      &:hover {
        background: #94a3b8;
      }
    }
  }
}

:deep(.v-field--focused) {
  .v-field__input {
    color: black !important;
  }
}

// Estilo para as dicas de markdown
.markdown-tips {
  span {
    // cursor: pointer;
    transition: all 0.2s ease;

    // &:hover {
    //   background-color: var(--v-theme-primary) !important;
    //   color: white !important;
    // }
  }
}
</style>
